const useBannerCarouselBlock = (blockElement: HTMLElement): void => {
  const slides = Array.from(blockElement.querySelectorAll('.banner-carousel-block__slide')) as HTMLElement[];
  const images = slides
    .map((slide) => slide.querySelector('.banner-carousel-block__image') as HTMLImageElement | null)
    .filter((image): image is HTMLImageElement => image !== null);
  const toggleButtons = slides
    .map((slide) => slide.querySelector('.banner-carousel-block__toggle') as HTMLButtonElement | null)
    .filter((button): button is HTMLButtonElement => button !== null);
  const intervalMs = Number.parseInt(blockElement.dataset.intervalMs ?? '5000', 10);
  const interval = Number.isFinite(intervalMs) ? intervalMs : 5000;
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  if (slides.length === 0) {
    return;
  }

  let currentIndex = 0;
  let isPaused = reducedMotionQuery.matches;
  let timerId: number | null = null;

  const hideAllSlides = (): void => {
    slides.forEach((slide) => {
      slide.style.display = 'none';
      slide.setAttribute('aria-hidden', 'true');
    });
  };

  const preloadImages = (): void => {
    images.forEach((image) => {
      if (image.complete) {
        return;
      }

      const preloadSource = image.currentSrc || image.src;
      if (!preloadSource) {
        return;
      }

      const preloader = new Image();
      preloader.src = preloadSource;
    });
  };

  const render = (): void => {
    slides.forEach((slide, index) => {
      const isActive = index === currentIndex;

      slide.style.display = isActive ? 'block' : 'none';
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });
  };

  const setButtonLabel = (): void => {
    toggleButtons.forEach((toggleButton) => {
      const pauseLabel = toggleButton.dataset.pauseLabel ?? 'Pause';
      const playLabel = toggleButton.dataset.playLabel ?? 'Play';
      const nextLabel = isPaused ? playLabel : pauseLabel;

      toggleButton.setAttribute('aria-label', nextLabel);
      toggleButton.setAttribute('aria-pressed', isPaused ? 'true' : 'false');
      toggleButton.classList.toggle('icon--play', isPaused);
      toggleButton.classList.toggle('icon--pause', !isPaused);
    });
  };

  const stop = (): void => {
    if (timerId !== null) {
      window.clearInterval(timerId);
      timerId = null;
    }
  };

  const showSlide = (index: number): void => {
    currentIndex = index;
    render();
  };

  const start = (): void => {
    if (slides.length <= 1 || isPaused || timerId !== null) {
      return;
    }

    timerId = window.setInterval(() => {
      const nextIndex = (currentIndex + 1) % slides.length;
      showSlide(nextIndex);
    }, interval);
  };

  const handleToggleClick = (): void => {
    isPaused = !isPaused;

    if (isPaused) {
      stop();
    } else {
      start();
    }

    setButtonLabel();
  };

  // Listen for changes to the prefers-reduced-motion media query and update the carousel behavior accordingly
  const handleReducedMotionChange = ({ matches }: MediaQueryListEvent): void => {
    isPaused = matches;

    if (isPaused) {
      stop();
    } else {
      start();
    }

    setButtonLabel();
  };

  const initialize = (): void => {
    hideAllSlides();
    render();
    preloadImages();

    if (slides.length === 1) {
      toggleButtons.forEach((toggleButton) => {
        toggleButton.hidden = true;
      });

      return;
    }

    toggleButtons.forEach((toggleButton) => {
      toggleButton.addEventListener('click', handleToggleClick);
    });

    reducedMotionQuery.addEventListener('change', handleReducedMotionChange);

    setButtonLabel();
    start();
  };

  initialize();
};

const bannerCarouselBlockElements = document.querySelectorAll('.banner-carousel-block__content') as NodeListOf<HTMLElement>;

if (bannerCarouselBlockElements.length > 0) {
  bannerCarouselBlockElements.forEach((blockElement) => {
    useBannerCarouselBlock(blockElement);
  });
}
